/**
 * Static display metadata for the Regulatory module.
 *
 * Stages are NOT here — they're user-configurable rows fetched from
 * `/api/regulatory/stages` and rendered with their own `color`/`name`.
 * This file holds the fixed enums (category, priority) and the bridge
 * from a stage's hex color to the KanbanStage `accent` slot.
 */
import type { ChipVariant } from '@/components/ui'
import type { Category, Priority } from '../api/types'

export const CATEGORY_LABELS: Record<Category, string> = {
  coa: 'Certificate of Analysis',
  sds: 'Safety Data Sheet',
  tds: 'Technical Data Sheet',
  compliance_statement: 'Compliance Statement',
  spec_dossier: 'Spec / Dossier',
  country_of_origin: 'Country of Origin',
  reach_tsca: 'REACH / TSCA',
  inci: 'INCI Declaration',
  vendor_doc_request: 'Vendor Doc Request',
  change_notification: 'Change Notification',
  other: 'Other',
}

export const CATEGORY_SHORT: Record<Category, string> = {
  coa: 'COA',
  sds: 'SDS',
  tds: 'TDS',
  compliance_statement: 'Compliance',
  spec_dossier: 'Spec/Dossier',
  country_of_origin: 'Country',
  reach_tsca: 'REACH/TSCA',
  inci: 'INCI',
  vendor_doc_request: 'Vendor Doc',
  change_notification: 'Change Notif.',
  other: 'Other',
}

export const CATEGORY_OPTIONS: { value: Category; label: string }[] = (
  Object.keys(CATEGORY_LABELS) as Category[]
).map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
}

export const PRIORITY_VARIANT: Record<Priority, ChipVariant> = {
  low: 'neutral',
  medium: 'info',
  high: 'warning',
  urgent: 'danger',
}

export const PRIORITY_OPTIONS: { value: Priority; label: string }[] = (
  ['urgent', 'high', 'medium', 'low'] as Priority[]
).map((p) => ({ value: p, label: PRIORITY_LABELS[p] }))

/**
 * Map a stage's color (server-chosen, arbitrary) onto the 4-value
 * KanbanStage `accent` enum used by the column-header border tone. We
 * parse the color into RGB and classify by dominant channel rather than
 * matching literal color values (the tokens-only contract bans hardcoded
 * color literals anywhere under src/). The full color is surfaced
 * separately via a `--stage-color` CSS custom property on the card —
 * that's user data, not a hardcoded design token.
 */
export function accentForHex(
  color: string | null,
): 'neutral' | 'success' | 'warning' | 'danger' {
  const rgb = parseRgb(color)
  if (!rgb) return 'neutral'
  const [r, g, b] = rgb
  // Near-grey (low channel spread) → neutral.
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  if (max - min < 40) return 'neutral'
  // Red dominant + green low → danger; red+green high (amber) → warning;
  // green dominant → success; else neutral (blues/purples).
  if (r >= g && r >= b) {
    return g > b + 40 ? 'warning' : 'danger'
  }
  if (g >= r && g >= b) return 'success'
  return 'neutral'
}

/** Parse "#rgb" / "#rrggbb" into [r,g,b] (0-255), or null. */
function parseRgb(color: string | null): [number, number, number] | null {
  if (!color) return null
  const s = color.trim().replace(/^#/, '')
  if (s.length === 3) {
    const r = parseInt(s[0] + s[0], 16)
    const g = parseInt(s[1] + s[1], 16)
    const b = parseInt(s[2] + s[2], 16)
    return Number.isNaN(r + g + b) ? null : [r, g, b]
  }
  if (s.length === 6) {
    const r = parseInt(s.slice(0, 2), 16)
    const g = parseInt(s.slice(2, 4), 16)
    const b = parseInt(s.slice(4, 6), 16)
    return Number.isNaN(r + g + b) ? null : [r, g, b]
  }
  return null
}
